// ============================================================
// FYUNOR — SEO Framework
// Resolves SEO metadata for a calculator + locale, and applies it to
// the document. Never hardcode SEO in page components.
// ============================================================

import { getCountry } from "@/lib/locale/countries";
import { getRegion } from "@/lib/locale/regions";

const BASE_SITE_NAME = "Fyunor";
export const PRODUCTION_ORIGIN = "https://fyunor.com";
const LOGO_URL = `${PRODUCTION_ORIGIN}/logo.svg`;
const OG_IMAGE_URL = "https://media.base44.com/images/public/6a7629cb4dcda6ad3ef679a6/41e9201e3_Fyunor-og-image.png";

function toAbsoluteUrl(path) {
  if (!path) return undefined;
  return path.startsWith("http") ? path : `${PRODUCTION_ORIGIN}${path}`;
}

// ============================================================
// STRUCTURED DATA (JSON-LD) BUILDERS
// ============================================================

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BASE_SITE_NAME,
    url: PRODUCTION_ORIGIN,
    logo: LOGO_URL,
  };
}

export function buildWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BASE_SITE_NAME,
    url: PRODUCTION_ORIGIN,
  };
}

export function buildWebPageSchema({ title, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    isPartOf: {
      "@type": "WebSite",
      name: BASE_SITE_NAME,
      url: PRODUCTION_ORIGIN,
    },
  };
}

export function buildBreadcrumbSchema(breadcrumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.label,
      item: b.path.startsWith("http") ? b.path : `${PRODUCTION_ORIGIN}${b.path}`,
    })),
  };
}

export function buildFAQSchema(faq) {
  if (!faq || !faq.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

export function buildSoftwareApplicationSchema({ name, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    url,
    description,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };
}

/**
 * Resolve an SEO config object for rendering.
 * {
 *   title, description, canonical, openGraph, twitter,
 *   breadcrumbs[], structuredData, robots, lastUpdated
 * }
 */
export function resolveSEO(definition, { country, region } = {}) {
  const seo = definition?.seo || {};
  const countryDef = country ? getCountry(country) : null;
  const regionDef = country && region ? getRegion(country, region) : null;

  const localeSegment = country ? `/${country.toLowerCase()}` : "";
  const regionSegment = region ? `/${region.toLowerCase()}` : "";
  const path = `${localeSegment}${regionSegment}/calculators/${definition?.slug || ""}`;

  const title = seo.title || (definition ? `${definition.name} | ${BASE_SITE_NAME}` : BASE_SITE_NAME);
  const description = seo.description || definition?.description || "";

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Calculators", path: "/calculators" },
  ];
  if (countryDef) breadcrumbs.push({ label: countryDef.name, path: `/${country.toLowerCase()}/calculators` });
  if (regionDef) breadcrumbs.push({ label: regionDef.name, path: `${localeSegment}/${region.toLowerCase()}/calculators` });
  if (definition) breadcrumbs.push({ label: definition.name, path });

  const canonicalUrl = `${PRODUCTION_ORIGIN}${path}`;

  const structuredData = [
    buildWebPageSchema({ title, description, url: canonicalUrl }),
    buildBreadcrumbSchema(breadcrumbs),
    buildSoftwareApplicationSchema({ name: definition?.name, description, url: canonicalUrl }),
  ];

  const faqSchema = buildFAQSchema(definition?.faq);
  if (faqSchema) structuredData.push(faqSchema);

  return {
    title,
    description,
    canonical: path,
    openGraph: {
      title,
      description,
      type: "website",
      siteName: BASE_SITE_NAME,
      url: canonicalUrl,
      image: OG_IMAGE_URL,
      ...seo.openGraph,
    },
    twitter: { card: "summary_large_image", title, description, image: OG_IMAGE_URL, ...seo.twitter },
    breadcrumbs,
    structuredData,
    robots: seo.index === false ? "noindex, nofollow" : "index, follow",
  };
}

/** Apply an SEO config to the live document (title, meta, canonical). */
export function applySEO(seo) {
  if (!seo) return;
  if (seo.title) document.title = seo.title;
  setMeta("description", seo.description);
  setMeta("og:title", seo.openGraph?.title || seo.title, true);
  setMeta("og:description", seo.openGraph?.description || seo.description, true);
  setMeta("og:url", toAbsoluteUrl(seo.canonical), true);
  setMeta("og:type", seo.openGraph?.type || "website", true);
  setMeta("og:site_name", seo.openGraph?.siteName || BASE_SITE_NAME, true);
  setMeta("og:image", seo.openGraph?.image || OG_IMAGE_URL, true);
  setMeta("twitter:card", seo.twitter?.card || "summary_large_image");
  setMeta("twitter:title", seo.twitter?.title || seo.title);
  setMeta("twitter:description", seo.twitter?.description || seo.description);
  setMeta("twitter:image", seo.twitter?.image || OG_IMAGE_URL);
  setCanonical(seo.canonical);
  setRobots(seo.robots);
  setStructuredData(seo.structuredData);
}

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  // Canonical must be absolute; resolve relative paths against the origin.
  const absolute = href.startsWith("http") ? href : `${PRODUCTION_ORIGIN}${href}`;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", absolute);
}

function setRobots(content) {
  if (!content) return;
  let el = document.head.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setStructuredData(schemas) {
  // Remove existing JSON-LD scripts to avoid duplicates on SPA navigation
  document.head.querySelectorAll('script[data-fyunor-jsonld]').forEach((el) => el.remove());
  if (!schemas || !Array.isArray(schemas)) return;
  schemas.forEach((schema) => {
    if (!schema) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-fyunor-jsonld", "");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}